import { useMemo, useState, type FormEvent } from 'react';
import { format, parseISO } from 'date-fns';
import TimePicker from '../ui/TimePicker';
import TimeModeToggle from './TimeModeToggle';
import { useTranslation } from 'react-i18next';
import { itineraryApi } from '../../api/itinerary.api';
import type { NextDayUpdate } from '../../api/itinerary.api';
import { useDateLocale } from '../../utils/dateLocale';
import type { Activity, ActivityType, ItineraryDay, Venue, VenueCategory } from '../../types';
import { Button } from '../ui/Button';
import ConfirmModal from '../ui/ConfirmModal';
import { toast } from '../ui/Toast';
import { Check, X, Icon } from '../ui/Icon';
import AddressAutocomplete from '../ui/AddressAutocomplete';
import KindSelect from '../ui/KindSelect';
import DurationInput from '../ui/DurationInput';

const TYPES: ActivityType[] = ['RESTAURANT', 'CAFE', 'MONUMENT', 'VIEWPOINT', 'NEIGHBORHOOD', 'PARK', 'SHOP', 'EXPERIENCE', 'BEACH', 'NATURE', 'TRANSPORT', 'PARKING', 'ACCOMMODATION', 'OTHER'];

const CAT_TO_TYPE: Partial<Record<VenueCategory, ActivityType>> = {
  RESTAURANT: 'RESTAURANT', CAFE: 'CAFE', ATTRACTION: 'MONUMENT',
  VIEWPOINT: 'VIEWPOINT', NEIGHBORHOOD: 'NEIGHBORHOOD', PARK: 'PARK',
  SHOP: 'SHOP', EXPERIENCE: 'EXPERIENCE', TRANSPORT_HUB: 'TRANSPORT',
  BEACH: 'BEACH', NATURE: 'NATURE', PARKING: 'PARKING', OTHER: 'OTHER',
};

const CAT_STYLE: Record<VenueCategory, string> = {
  RESTAURANT:    'bg-orange-100 text-orange-700',
  CAFE:          'bg-amber-100 text-amber-700',
  ATTRACTION:    'bg-violet-100 text-violet-700',
  VIEWPOINT:     'bg-sky-100 text-sky-700',
  NEIGHBORHOOD:  'bg-indigo-100 text-indigo-700',
  PARK:          'bg-green-100 text-green-700',
  SHOP:          'bg-pink-100 text-pink-700',
  EXPERIENCE:    'bg-teal-100 text-teal-700',
  TRANSPORT_HUB: 'bg-slate-100 text-slate-600',
  BEACH:         'bg-cyan-100 text-cyan-700',
  NATURE:        'bg-lime-100 text-lime-700',
  PARKING:       'bg-blue-100 text-blue-700',
  OTHER:         'bg-stone-200 text-stone-600',
};

interface Props {
  tripId: string;
  dayId: string;
  existing?: Activity;
  venues?: Venue[];
  // Every day of the trip — enables the day picker when editing, so a card that
  // landed on the wrong day can be moved without deleting and re-creating it.
  days?: ItineraryDay[];
  // `venue` is set only when saving the activity also added its place to the
  // trip's Places list — the tab showing both has to pick it up.
  onSaved: (activity: Activity, nextDayUpdate: NextDayUpdate | null, dayActivities?: Activity[] | null, venue?: Venue | null) => void;
  onCancel: () => void;
  onDelete?: () => void;
}

export default function ActivityForm({ tripId, dayId, existing, venues, days, onSaved, onCancel, onDelete }: Props) {
  const { t } = useTranslation();
  const dateLocale = useDateLocale();
  const [submitted, setSubmitted] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const err = (bad: boolean) => bad ? ' input-error' : '';
  // ADR-0010: a day only has anchors once it has a computed schedule to anchor
  // against. Without `days` we cannot tell, and not offering the checkbox is the
  // safe read — the hour still works, it is just not pinned.
  const dayHasSchedule = !!days?.find(d => d.id === dayId)?.startTime;
  const tripCustomTypes = useMemo(
    () => (days ?? []).flatMap(d => d.activities.map(a => a.customType ?? '')),
    [days],
  );

  const [form, setForm] = useState({
    title:        existing?.title ?? '',
    type:         existing?.type ?? ('EXPERIENCE' as ActivityType),
    customType:   existing?.customType ?? '',
    startTime:    existing?.startTime ?? '',
    startTimeLocked: existing?.startTimeLocked ?? false,
    durationMins: existing?.durationMins ?? '',
    venueName:    existing?.venueName ?? '',
    address:      existing?.address ?? '',
    placeId:      existing?.placeId ?? '',
    // Resolved by the place picker (already paid for in that lookup) — sent so
    // neither the activity pin nor the place it saves has to be geocoded again.
    latitude:     existing?.latitude ?? null as number | null,
    longitude:    existing?.longitude ?? null as number | null,
    bookingUrl:   existing?.bookingUrl ?? '',
    notes:        existing?.notes ?? '',
    venueId:      existing?.venueId ?? '',
    isOptional:   existing?.isOptional ?? false,
  });
  const [loading, setLoading]       = useState(false);

  // ── Day picker (editing only) ───────────────────────────────────────────
  // The origin card is a mirror of the previous day's last stop, and a stay
  // produces one card per night slept — neither has a day of its own to move.
  const [targetDayId, setTargetDayId] = useState(existing?.dayId ?? dayId);
  const canMove   = !!existing && !existing.isOrigin && !existing.hotelId && (days?.length ?? 0) > 1;
  const stayNight = !!existing?.hotelId && !existing.isOrigin && (days?.length ?? 0) > 1;
  // A flight/booking card is generated from its booking, so moving it moves the
  // booking's date too — otherwise the next sync would drag the card back.
  const movesBooking = !!existing && !!(existing.flightId || existing.bookingId);
  const targetDay = days?.find(d => d.id === targetDayId);
  const isMoving  = canMove && !!existing && targetDayId !== existing.dayId;
  // ────────────────────────────────────────────────────────────────────────

  // ── Venue picker state (new activities only) ────────────────────────────
  const [selectedVenue, setSelectedVenue] = useState<Venue | null>(null);
  const [venueQuery, setVenueQuery]       = useState('');

  const filteredVenues = useMemo(() => {
    if (!venues) return [];
    const q = venueQuery.toLowerCase();
    return q
      ? venues.filter(v => v.name.toLowerCase().includes(q) || (v.zone ?? '').toLowerCase().includes(q))
      : venues;
  }, [venues, venueQuery]);

  function selectVenue(v: Venue) {
    setSelectedVenue(v);
    setVenueQuery('');
    setForm(f => ({
      ...f,
      title:     f.title || v.name,
      type:      CAT_TO_TYPE[v.category] ?? f.type,
      customType: v.category === 'OTHER' ? (v.customCategory ?? '') : '',
      venueName: v.name,
      address:   v.address ?? f.address,
      placeId:   v.placeId ?? f.placeId,
      latitude:  v.latitude,
      longitude: v.longitude,
      venueId:   v.id,
    }));
  }

  function clearVenue() {
    setSelectedVenue(null);
    setForm(f => ({ ...f, venueId: '', venueName: '', placeId: '', latitude: null, longitude: null }));
  }
  // ────────────────────────────────────────────────────────────────────────

  function update(field: string, value: string) {
    setForm(f => ({ ...f, [field]: value }));
  }

  /**
   * `keepOpen` is what turns adding five stops from five modal round trips into
   * one. The form resets to a blank stop on the same day rather than closing;
   * the parent still gets every `onSaved`, so the list behind fills in as you go.
   */
  async function save(keepOpen = false) {
    setLoading(true);
    try {
      const keepsPlace = !!form.address;
      const data = {
        ...form,
        durationMins: form.durationMins ? parseInt(form.durationMins as string) : null,
        // ADR-0020: the server drops it for any type but OTHER anyway.
        customType:   form.type === 'OTHER' ? (form.customType.trim() || null) : null,
        startTime:    form.startTime    || null,
        startTimeLocked: !!form.startTime && form.startTimeLocked,
        venueName:    form.venueName    || null,
        address:      form.address      || null,
        // Clearing the address must also drop placeId — otherwise the server's
        // enrichActivityFromPlaceId() re-fetches and restores the address from it.
        placeId:      keepsPlace ? (form.placeId || null) : null,
        latitude:     keepsPlace ? form.latitude  : null,
        longitude:    keepsPlace ? form.longitude : null,
        bookingUrl:   form.bookingUrl   || null,
        notes:        form.notes        || null,
        venueId:      form.venueId      || undefined,
      };
      let activity: Activity;
      let nextDayUpdate: NextDayUpdate | null = null;
      let dayActivities: Activity[] | null = null;
      let venue: Venue | null = null;
      if (existing) {
        activity = await itineraryApi.updateActivity(tripId, existing.dayId, existing.id, isMoving ? { ...data, dayId: targetDayId } : data);
        toast(
          isMoving ? t('itinerary.toast.movedToDay', { n: targetDay?.dayNumber }) : t('itinerary.toast.updated'),
          'success',
        );
      } else {
        const result = await itineraryApi.createActivity(tripId, dayId, data);
        activity = result.activity;
        nextDayUpdate = result.nextDayUpdate;
        dayActivities = result.dayActivities ?? null;
        // The stop pinned a place the trip didn't have saved, so it was added to
        // Places too — say so, or a new entry appears in that tab out of nowhere.
        venue = result.venue ?? null;
        toast(venue ? t('itinerary.toast.addedWithPlace', { name: venue.name }) : t('itinerary.toast.added'), 'success');
      }
      onSaved(activity, nextDayUpdate, dayActivities, venue);
      if (keepOpen) resetForNext();
    } catch {
      toast(t('itinerary.toast.failed'), 'error');
    } finally {
      setLoading(false);
    }
  }

  /** Blank slate for the next stop, keeping nothing that belonged to this one. */
  function resetForNext() {
    setForm(f => ({
      ...f,
      title: '', startTime: '', startTimeLocked: false, durationMins: '', venueName: '', address: '',
      placeId: '', latitude: null, longitude: null, bookingUrl: '', notes: '',
      venueId: '', isOptional: false,
    }));
    setSelectedVenue(null);
    setVenueQuery('');
    setSubmitted(false);
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitted(true);
    if (!form.title.trim()) return;
    await save();
  }

  async function saveAndAddAnother() {
    setSubmitted(true);
    if (!form.title.trim() || loading) return;
    await save(true);
  }

  const showPicker = !existing && venues && venues.length > 0;

  return (
    <form onSubmit={handleSubmit} className="space-y-3">

      {/* ── Saved places picker ──────────────────────────────────────────── */}
      {showPicker && (
        <div className="space-y-1.5">
          <p className="text-xs font-semibold text-slate-600">{t('itinerary.form.fromPlaces')}</p>

          {selectedVenue ? (
            /* Selected chip */
            <div className="flex items-center gap-2.5 px-3 py-2 bg-teal-50 border border-teal-200 rounded-xl">
              <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-md shrink-0 ${CAT_STYLE[selectedVenue.category]}`}>
                {selectedVenue.category.charAt(0)}
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-teal-800 truncate">{selectedVenue.name}</p>
                {selectedVenue.zone && (
                  <p className="text-[11px] text-teal-600/70 truncate">{selectedVenue.zone}</p>
                )}
              </div>
              <button
                type="button"
                onClick={clearVenue}
                className="w-5 h-5 rounded-full hover:bg-teal-200/60 flex items-center justify-center transition-colors cursor-pointer shrink-0"
                aria-label={t('common.cancel')}
              >
                <Icon as={X} size={11} className="text-teal-600" />
              </button>
            </div>
          ) : (
            /* Picker list */
            <>
              <input
                type="text"
                className="input text-xs py-1.5"
                placeholder={t('itinerary.form.filterPlaces')}
                value={venueQuery}
                onChange={e => setVenueQuery(e.target.value)}
              />
              <div className="max-h-44 overflow-y-auto space-y-1 pr-0.5 -mr-1">
                {filteredVenues.length > 0 ? (
                  filteredVenues.map(v => (
                    <button
                      key={v.id}
                      type="button"
                      onClick={() => selectVenue(v)}
                      className="w-full flex items-center gap-2.5 px-3 py-2 rounded-xl border border-slate-200 bg-white hover:border-teal-300 hover:bg-teal-50 transition-all cursor-pointer text-left"
                    >
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-md shrink-0 ${CAT_STYLE[v.category]}`}>
                        {v.category.charAt(0)}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold text-slate-800 truncate">{v.name}</p>
                        {v.zone && <p className="text-[11px] text-slate-400 truncate">{v.zone}</p>}
                      </div>
                      {v.visited && <Icon as={Check} size={12} className="text-green-500 shrink-0" />}
                    </button>
                  ))
                ) : (
                  <p className="text-xs text-slate-400 py-3 text-center">{t('itinerary.form.noMatchPlaces')}</p>
                )}
              </div>
              <div className="border-t border-slate-100 pt-1" />
            </>
          )}
        </div>
      )}

      {/* ── Standard fields ──────────────────────────────────────────────── */}
      <div>
        <label className="label">{t('itinerary.form.titleField')} *</label>
        <input
          type="text"
          className={'input' + err(submitted && !form.title.trim())}
          value={form.title}
          onChange={e => update('title', e.target.value)}
          placeholder={t('itinerary.form.titlePlaceholder')}
        />
      </div>
      <div>
        <label className="label">{t('itinerary.form.type')}</label>
        <KindSelect
          ariaLabel={t('itinerary.form.type')}
          options={TYPES.map(type => ({ value: type, label: t(`itinerary.types.${type}` as Parameters<typeof t>[0]) }))}
          otherValue="OTHER"
          value={form.type}
          customName={form.customType}
          existingNames={tripCustomTypes}
          onChange={(type, customType) => setForm(f => ({ ...f, type, customType }))}
          labels={{
            yours: t('itinerary.form.yourTypes'), create: t('itinerary.form.newType'),
            name: t('itinerary.form.newTypeName'), namePlaceholder: t('itinerary.form.newTypePlaceholder'),
          }}
        />
      </div>
      {(canMove || stayNight) && (
        <div>
          <label className="label">{t('itinerary.form.day')}</label>
          <select
            className="input"
            aria-label={t('itinerary.form.day')}
            value={targetDayId}
            disabled={stayNight}
            onChange={e => setTargetDayId(e.target.value)}
          >
            {days!.map(d => (
              <option key={d.id} value={d.id}>
                {t('itinerary.dayLabel', { n: d.dayNumber })} · {format(parseISO(d.date), 'EEE d MMM', { locale: dateLocale })}
                {d.title ? ` — ${d.title}` : ''}
              </option>
            ))}
          </select>
          {stayNight
            ? <p className="mt-1 text-2xs text-slate-400">{t('itinerary.form.dayStayHint')}</p>
            : movesBooking && <p className="mt-1 text-2xs text-slate-400">{t('itinerary.form.dayBookingHint')}</p>}
        </div>
      )}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="label">{t('itinerary.form.startTime')}</label>
          {/* ADR-0010: an hour chosen by hand is an anchor — reordering must not
              move it. Only a change pins it; opening a stop with a computed hour
              and saving something else leaves the cascade in charge. */}
          <TimePicker value={form.startTime} onChange={v => setForm(f => ({ ...f, startTime: v, startTimeLocked: true }))} />
          {/* ADR-0010: only worth offering once there is an hour to pin, and only
              on a day whose schedule is computed — elsewhere every hour is
              already fixed and the toggle would promise a distinction that
              does not exist. */}
          {form.startTime && dayHasSchedule && (
            <TimeModeToggle locked={form.startTimeLocked} onChange={locked => setForm(f => ({ ...f, startTimeLocked: locked }))} />
          )}
        </div>
        <div>
          <label className="label">{t('itinerary.form.duration')}</label>
          <DurationInput
            label={t('itinerary.form.duration')}
            value={form.durationMins === '' ? null : Number(form.durationMins)}
            onChange={mins => update('durationMins', mins == null ? '' : String(mins))}
          />
        </div>
      </div>
      <div>
        <label className="label">{t('venues.form.address')}</label>
        <AddressAutocomplete
          value={form.address}
          onChange={v => setForm(f => ({ ...f, address: v }))}
          onSelect={d => setForm(f => ({
            ...f,
            address:   d.address || d.name,
            placeId:   d.placeId,
            venueName: d.name || f.venueName,
            latitude:  d.lat,
            longitude: d.lng,
          }))}
          placeholder={t('venues.form.searchPlace')}
        />
      </div>
      <div>
        <label className="label">{t('itinerary.form.bookingUrl')}</label>
        <input type="url" className="input" value={form.bookingUrl} onChange={e => update('bookingUrl', e.target.value)} placeholder="https://..." />
      </div>
      <div>
        <label className="label">{t('itinerary.form.notes')}</label>
        <textarea className="input" rows={2} value={form.notes} onChange={e => update('notes', e.target.value)} placeholder={t('itinerary.form.notesPlaceholder')} />
      </div>
      <label className="flex items-start gap-3 cursor-pointer select-none p-2 -m-2 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
        <input
          type="checkbox"
          checked={form.isOptional}
          onChange={e => setForm(f => ({ ...f, isOptional: e.target.checked }))}
          className="mt-0.5 h-4 w-4 rounded accent-slate-800 border-slate-300 shrink-0"
        />
        <span className="min-w-0">
          <span className="block text-xs font-semibold text-slate-700 dark:text-slate-200">{t('itinerary.optional.label')}</span>
          <span className="block text-2xs text-slate-400 leading-relaxed">{t('itinerary.optional.hint')}</span>
        </span>
      </label>

      <div className="flex gap-3 pt-2">
        <Button type="submit" variant="cta" className="flex-1 disabled:opacity-50 disabled:pointer-events-none" loading={loading}>
          {existing ? t('itinerary.form.update') : t('itinerary.form.addActivity')}
        </Button>
        <button type="button" className="btn-secondary" onClick={onCancel}>{t('common.cancel')}</button>
      </div>

      {/* Only when creating: "another one" makes no sense while editing a card. */}
      {!existing && (
        <button
          type="button"
          onClick={saveAndAddAnother}
          disabled={loading}
          className="w-full text-center text-xs font-semibold text-teal-600 dark:text-teal-400 hover:text-teal-700 dark:hover:text-teal-300 disabled:opacity-50 cursor-pointer"
        >
          {t('itinerary.form.saveAndAddAnother')}
        </button>
      )}

      {/* Delete link (editing only) */}
      {onDelete && (
        <button type="button" onClick={() => setConfirmDelete(true)} className="w-full text-xs text-red-400 hover:text-red-600 transition-colors cursor-pointer pt-1">
          {t('itinerary.delete.title')}
        </button>
      )}
      <ConfirmModal
        open={confirmDelete}
        title={t('itinerary.delete.title')}
        body={t('itinerary.delete.body')}
        onConfirm={() => { onDelete?.(); setConfirmDelete(false); }}
        onCancel={() => setConfirmDelete(false)}
      />
    </form>
  );
}
